import type { TimedMusicXmlNote } from "./songEngine";
import { POLYPHONIC_EVENT_MIN_NOTES } from "./practiceEventScoring";

/** Con micrófono: hasta 4 voces por ataque (triadas y arpegios típicos). */
export const MIC_PRACTICE_MAX_VOICES = 4;

/** Con guitarra por cable (entrada de línea): hasta este número de voces en practicar. */
export const LINE_PRACTICE_MAX_VOICES = 4;

export type SongPolyClassification = "melodic" | "mixed" | "dense-poly";

export type GuitarInputMode = "mic" | "line";

export interface SongDetectionProfile {
  maxVoicesPerAttack: number;
  /** Eventos de ataque con ≥2 notas simultáneas. */
  polyEventCount: number;
  /** Eventos con ≥3 voces (acordes densos). */
  densePolyEventCount: number;
  classification: SongPolyClassification;
}

export function analyzeSongDetectionProfile(
  notes: TimedMusicXmlNote[]
): SongDetectionProfile {
  const byAttack = new Map<number, number>();

  for (const n of notes) {
    if (n.isRest || n.midi <= 0) continue;
    byAttack.set(n.startTime, (byAttack.get(n.startTime) ?? 0) + 1);
  }

  let maxVoicesPerAttack = 0;
  let polyEventCount = 0;
  let densePolyEventCount = 0;

  for (const count of byAttack.values()) {
    maxVoicesPerAttack = Math.max(maxVoicesPerAttack, count);
    if (count >= POLYPHONIC_EVENT_MIN_NOTES) polyEventCount += 1;
    if (count >= 3) densePolyEventCount += 1;
  }

  let classification: SongPolyClassification = "melodic";
  if (maxVoicesPerAttack >= 3 || densePolyEventCount > 0) {
    classification = "dense-poly";
  } else if (maxVoicesPerAttack >= POLYPHONIC_EVENT_MIN_NOTES || polyEventCount > 0) {
    classification = "mixed";
  }

  return {
    maxVoicesPerAttack,
    polyEventCount,
    densePolyEventCount,
    classification,
  };
}

export function practiceAllowsMicScoring(
  profile: SongDetectionProfile,
  inputMode: GuitarInputMode
): boolean {
  const limit = inputMode === "line" ? LINE_PRACTICE_MAX_VOICES : MIC_PRACTICE_MAX_VOICES;
  return profile.maxVoicesPerAttack <= limit;
}

export function learnChordHintVisible(profile: SongDetectionProfile): boolean {
  return profile.classification === "dense-poly" || profile.maxVoicesPerAttack >= 3;
}
